
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface RegionAirport {
  id: number;
  name: string;
  location: string;
  iata_code: string | null;
  reviews: {
    average_score: number;
    total_reviews: number;
    hand_check_success_rate: number;
  };
}

export function useRegionAirports(region: string) {
  return useQuery<RegionAirport[]>({
    queryKey: ['airports', region],
    queryFn: async () => {
      if (!region) return [];
      
      // Map regions to their constituent countries for more accurate filtering
      const regionCountries: Record<string, string[]> = {
        'Europe': [
          'albania', 'andorra', 'austria', 'belarus', 'belgium', 'bosnia', 'bulgaria', 
          'croatia', 'czechia', 'czech republic', 'denmark', 'estonia', 'finland', 'france', 
          'germany', 'greece', 'hungary', 'iceland', 'ireland', 'italy', 'kosovo', 'latvia', 
          'liechtenstein', 'lithuania', 'luxembourg', 'malta', 'moldova', 'monaco', 
          'montenegro', 'netherlands', 'north macedonia', 'norway', 'poland', 'portugal', 
          'romania', 'russia', 'san marino', 'serbia', 'slovakia', 'slovenia', 'spain', 
          'sweden', 'switzerland', 'ukraine', 'united kingdom', 'uk', 'vatican city'
        ],
        'North America': [
          'united states', 'usa', 'u.s.a.', 'canada', 'mexico', 'cuba', 'jamaica', 
          'haiti', 'dominican republic', 'puerto rico', 'bahamas', 'guatemala', 
          'belize', 'el salvador', 'honduras', 'nicaragua', 'costa rica', 'panama'
        ],
        'Asia': [
          'afghanistan', 'armenia', 'azerbaijan', 'bahrain', 'bangladesh', 'bhutan', 
          'brunei', 'cambodia', 'china', 'cyprus', 'georgia', 'india', 'indonesia', 
          'iran', 'iraq', 'israel', 'japan', 'jordan', 'kazakhstan', 'kuwait', 'kyrgyzstan', 
          'laos', 'lebanon', 'malaysia', 'maldives', 'mongolia', 'myanmar', 'nepal', 
          'north korea', 'oman', 'pakistan', 'palestine', 'philippines', 'qatar', 
          'saudi arabia', 'singapore', 'south korea', 'sri lanka', 'syria', 'taiwan', 
          'tajikistan', 'thailand', 'timor-leste', 'turkey', 'turkmenistan', 
          'united arab emirates', 'uae', 'uzbekistan', 'vietnam', 'yemen'
        ],
        'Oceania': [
          'australia', 'fiji', 'kiribati', 'marshall islands', 'micronesia', 
          'nauru', 'new zealand', 'palau', 'papua new guinea', 'samoa', 
          'solomon islands', 'tonga', 'tuvalu', 'vanuatu'
        ],
        'Africa': [
          'algeria', 'angola', 'benin', 'botswana', 'burkina faso', 'burundi', 
          'cabo verde', 'cameroon', 'central african republic', 'chad', 'comoros', 
          'congo', 'djibouti', 'egypt', 'equatorial guinea', 'eritrea', 'eswatini', 
          'ethiopia', 'gabon', 'gambia', 'ghana', 'guinea', 'guinea-bissau', 'ivory coast', 
          'kenya', 'lesotho', 'liberia', 'libya', 'madagascar', 'malawi', 'mali', 
          'mauritania', 'mauritius', 'morocco', 'mozambique', 'namibia', 'niger', 
          'nigeria', 'rwanda', 'são tomé and príncipe', 'senegal', 'seychelles', 
          'sierra leone', 'somalia', 'south africa', 'south sudan', 'sudan', 
          'tanzania', 'togo', 'tunisia', 'uganda', 'zambia', 'zimbabwe'
        ],
        'South America': [
          'argentina', 'bolivia', 'brazil', 'chile', 'colombia', 'ecuador', 
          'guyana', 'paraguay', 'peru', 'suriname', 'uruguay', 'venezuela'
        ]
      };
      
      // Get countries for the current region
      const countries = regionCountries[region] || [region.toLowerCase()];
      
      console.log(`Looking for airports in ${region} using ${countries.length} countries`);
      
      // Get all airports first
      const { data: allAirports, error } = await supabase
        .from('airports')
        .select('id, name, location, iata_code');
      
      if (error) throw error;
      
      // Enhanced filtering with strict location check
      const filteredAirports = allAirports.filter(airport => {
        const airportLocation = airport.location.toLowerCase();
        
        // Special handling for Fukuoka Airport to ensure it's in Asia only
        if (airport.name.includes('Fukuoka') && region !== 'Asia') {
          return false;
        }
        
        // More strict filtering - check for full country words to avoid partial matches
        return countries.some(country => {
          // Find the country as a standalone word or at the end of the string
          const regex = new RegExp(`\\b${country}\\b`, 'i');
          return regex.test(airportLocation);
        });
      });
      
      console.log(`Found ${filteredAirports.length} airports in ${region} after filtering`);
      
      // If no airports found, return empty array
      if (filteredAirports.length === 0) return [];
      
      // Get all reviews for these airports to calculate stats
      const airportNames = filteredAirports.map(airport => airport.name);
      
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('airport_name, film_friendly_score, hand_check_success')
        .in('airport_name', airportNames);
        
      if (reviewsError) throw reviewsError;
      
      // Calculate statistics for each airport
      return filteredAirports.map(airport => {
        const airportReviews = reviews?.filter(r => r.airport_name === airport.name) || [];
        const totalReviews = airportReviews.length;
        
        let averageScore = 0;
        let handCheckSuccessRate = 0;
        
        if (totalReviews > 0) {
          averageScore = airportReviews.reduce((sum, r) => sum + r.film_friendly_score, 0) / totalReviews;
          const successfulHandChecks = airportReviews.filter(r => r.hand_check_success).length;
          handCheckSuccessRate = (successfulHandChecks / totalReviews) * 100;
        }
        
        return {
          ...airport,
          reviews: {
            average_score: averageScore,
            total_reviews: totalReviews,
            hand_check_success_rate: handCheckSuccessRate
          }
        };
      });
    },
    enabled: !!region,
    staleTime: 5 * 60 * 1000, // Cache results for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
}
